import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Trophy, TrendingUp, Users, Medal, Crown, Award } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Leaderboard({ eventId, compact = false }) {
  const [leaderboard, setLeaderboard] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (eventId) {
      fetchLeaderboard();
    }
  }, [eventId]);

  const fetchLeaderboard = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/boost/leaderboard/${eventId}`);
      setLeaderboard(response.data);
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="animate-pulse bg-gray-100 rounded-xl p-6 h-48" />
    );
  }

  if (!leaderboard || leaderboard.leaderboard.length === 0) {
    return null; // Don't show if no promoters yet
  }

  const getRankIcon = (rank) => {
    switch (rank) {
      case 1:
        return <Crown className="w-5 h-5 text-yellow-500" />;
      case 2:
        return <Medal className="w-5 h-5 text-gray-400" />;
      case 3:
        return <Award className="w-5 h-5 text-amber-600" />;
      default:
        return <span className="w-5 h-5 flex items-center justify-center text-gray-500 font-medium">#{rank}</span>;
    }
  };

  const getRankBg = (rank) => {
    switch (rank) {
      case 1:
        return 'bg-gradient-to-r from-yellow-50 to-amber-50 border-yellow-200';
      case 2:
        return 'bg-gradient-to-r from-gray-50 to-slate-50 border-gray-200';
      case 3:
        return 'bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200';
      default:
        return 'bg-white border-gray-100';
    }
  };

  if (compact) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex items-center gap-2 mb-3">
          <Trophy className="w-5 h-5 text-orange-500" />
          <h3 className="font-semibold text-gray-900">Top Promoters</h3>
        </div>
        <div className="space-y-2">
          {leaderboard.leaderboard.slice(0, 3).map((entry) => (
            <div
              key={entry.referral_code}
              className={`flex items-center justify-between p-2 rounded-lg ${getRankBg(entry.rank)}`}
            >
              <div className="flex items-center gap-2">
                {getRankIcon(entry.rank)}
                <span className="text-sm font-medium text-gray-700">{entry.display_name}</span>
              </div>
              <span className="text-sm font-semibold text-orange-600">{entry.registrations} referrals</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-500 to-amber-500 p-6 text-white">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-white/20 rounded-lg">
            <Trophy className="w-6 h-6" />
          </div>
          <div>
            <h2 className="text-xl font-bold">Promoter Leaderboard</h2>
            <p className="text-orange-100 text-sm">{leaderboard.event_name}</p>
          </div>
        </div>
        
        {/* Stats */}
        <div className="grid grid-cols-2 gap-4 mt-6">
          <div className="bg-white/10 rounded-lg p-3">
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              <span className="text-sm">Total Promoters</span>
            </div>
            <p className="text-2xl font-bold mt-1">{leaderboard.total_promoters}</p>
          </div>
          <div className="bg-white/10 rounded-lg p-3">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4" />
              <span className="text-sm">Total Referrals</span>
            </div>
            <p className="text-2xl font-bold mt-1">{leaderboard.total_conversions}</p>
          </div>
        </div>
      </div>

      {/* Leaderboard List */}
      <div className="p-6">
        <div className="space-y-3">
          {leaderboard.leaderboard.map((entry) => (
            <div
              key={entry.referral_code}
              className={`flex items-center justify-between p-4 rounded-xl border ${getRankBg(entry.rank)} transition-all hover:shadow-md`}
            >
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-white border border-gray-200 flex items-center justify-center">
                  {getRankIcon(entry.rank)}
                </div>
                <div>
                  <p className="font-semibold text-gray-900">{entry.display_name}</p>
                  <p className="text-xs text-gray-500">{entry.conversion_rate}% conversion rate</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-xl font-bold text-orange-600">{entry.registrations}</p>
                <p className="text-xs text-gray-500">referrals</p>
              </div>
            </div>
          ))}
        </div>

        {leaderboard.leaderboard.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            <Trophy className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>Be the first to promote this event!</p>
          </div>
        )}
      </div>
    </div>
  );
}